import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { stripMarkdown } from '../utils/stripMarkdown';
import './VoiceModeOverlay.css';

const STATE = {
  IDLE:      'idle',
  LISTENING: 'listening',
  THINKING:  'thinking',
  SPEAKING:  'speaking',
};

const LABELS = {
  idle:      'Tap to speak',
  listening: 'Listening…',
  thinking:  'Thinking…',
  speaking:  'Speaking…',
};

export default function VoiceModeOverlay({
  chatbotId,
  chatbotName,
  chatbotConfig = {},
  conversationId,
  sessionId,
  onConversationId,
  onClose,
}) {
  const [phase, setPhase]       = useState(STATE.IDLE);
  const [userText, setUserText] = useState('');
  const [botText, setBotText]   = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const isRecordingRef   = useRef(false);
  const audioCtxRef      = useRef(null);
  const currentAudioRef  = useRef(null);

  const ttsProvider = chatbotConfig?.tts_provider || 'browser';

  // ── cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      window.speechSynthesis?.cancel();
      currentAudioRef.current?.pause();
    };
  }, []);

  // ── orb tap handler ────────────────────────────────────────────────────
  const handleOrbTap = () => {
    if (phase === STATE.IDLE)      return startRecording();
    if (phase === STATE.LISTENING) return stopRecording();
    if (phase === STATE.SPEAKING) {
      window.speechSynthesis?.cancel();
      currentAudioRef.current?.pause();
      currentAudioRef.current = null;
      setPhase(STATE.IDLE);
    }
    // THINKING — do nothing, waiting for API
  };

  // ── recording + VAD ────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pick the best MIME type this browser supports.
      // audio/webm is ideal (Chrome/Firefox); audio/mp4 is the only option on iOS Safari.
      // Never pass an unsupported mimeType to MediaRecorder — it throws NotSupportedError.
      const mimeType =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
        MediaRecorder.isTypeSupported('audio/webm')             ? 'audio/webm' :
        MediaRecorder.isTypeSupported('audio/mp4')              ? 'audio/mp4' :
        MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')  ? 'audio/ogg;codecs=opus' :
        '';

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      // Resume is required on iOS/mobile — AudioContext starts suspended after creation
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);

      // VAD thresholds:
      //   SPEECH_THRESHOLD  — RMS above this means the user IS speaking
      //   SILENCE_THRESHOLD — RMS below this (after speech) means silence
      //   SILENCE_DURATION  — ms of sustained silence before auto-stop
      //   MIN_RECORD_MS     — never auto-stop before this many ms
      //   MAX_RECORD_MS     — hard safety cutoff (stop even if no silence detected)
      const SPEECH_THRESHOLD  = 20;
      const SILENCE_THRESHOLD = 15;
      const SILENCE_DURATION  = 2000;
      const MIN_RECORD_MS     = 1000;
      const MAX_RECORD_MS     = 60000;
      let silenceStart   = null;
      let speechDetected = false; // only auto-stop AFTER user has actually spoken
      const startedAt    = Date.now();

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();
        // Use the MIME type the recorder actually chose (important on iOS where mp4 is forced)
        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        handleTranscribeAndSend(new Blob(audioChunksRef.current, { type: actualMime }));
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      isRecordingRef.current   = true;
      setPhase(STATE.LISTENING);

      const checkSilence = () => {
        if (!isRecordingRef.current) return;
        analyser.getByteFrequencyData(freqData);
        const rms = Math.sqrt(freqData.reduce((s, v) => s + v * v, 0) / freqData.length);
        const now = Date.now();

        // Hard cutoff — stop regardless
        if (now - startedAt >= MAX_RECORD_MS) { stopRecording(); return; }

        if (rms >= SPEECH_THRESHOLD) {
          // User is speaking — mark that speech happened, reset silence timer
          speechDetected = true;
          silenceStart   = null;
        } else if (speechDetected && rms < SILENCE_THRESHOLD) {
          // Silence AFTER speech — start or continue the silence countdown
          if (!silenceStart) silenceStart = now;
          else if (now - silenceStart >= SILENCE_DURATION && now - startedAt >= MIN_RECORD_MS) {
            stopRecording();
            return;
          }
        } else {
          // Mid-level noise (SILENCE_THRESHOLD ≤ rms < SPEECH_THRESHOLD) — reset silence timer
          silenceStart = null;
        }
        requestAnimationFrame(checkSilence);
      };
      requestAnimationFrame(checkSilence);
    } catch (err) {
      alert('Could not start recording: ' + (err.message || 'Unknown error') + '\nPlease allow microphone access in your browser settings.');
    }
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    mediaRecorderRef.current?.stop();
    setPhase(STATE.THINKING);
  };

  // ── transcribe → chat API → TTS ────────────────────────────────────────
  const handleTranscribeAndSend = async (blob) => {
    try {
      // 1. STT — 25 s timeout so a hung network call doesn't lock the UI
      const fd = new FormData();
      const ext = (blob.type || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
      fd.append('audio', blob, `recording.${ext}`);

      const controller = new AbortController();
      const timeoutId  = setTimeout(() => controller.abort(), 25000);
      let tRes, tData;
      try {
        tRes  = await fetch(`/api/voice/${chatbotId}/transcribe`, { method: 'POST', body: fd, signal: controller.signal });
        tData = await tRes.json();
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        setPhase(STATE.IDLE);
        setBotText(fetchErr.name === 'AbortError' ? 'Transcription timed out — please try again.' : 'Transcription failed — please try again.');
        return;
      }
      clearTimeout(timeoutId);

      if (!tRes.ok) {
        setPhase(STATE.IDLE);
        setBotText(tData?.message || 'Transcription failed — please try again.');
        return;
      }
      if (!tData.text?.trim()) {
        setPhase(STATE.IDLE);
        setBotText("Couldn't hear anything — please try again.");
        return;
      }

      const spoken = tData.text.trim();
      setUserText(spoken);
      setBotText('');

      // 2. Chat
      const cRes = await axios.post(`/api/chat/${chatbotId}`, {
        message: spoken,
        conversation_id: conversationId,
        user_session_id: sessionId,
      });
      onConversationId?.(cRes.data.conversation_id);
      const reply = cRes.data.message;
      setBotText(reply);

      // 3. TTS — strip markdown before speaking so "**bold**" isn't read aloud
      await speakText(stripMarkdown(reply));
    } catch {
      setPhase(STATE.IDLE);
    }
  };

  const speakText = async (text) => {
    if (!text || ttsProvider === 'none') { setPhase(STATE.IDLE); return; }
    setPhase(STATE.SPEAKING);

    if (ttsProvider === 'browser') {
      await new Promise((resolve) => {
        const utt  = new SpeechSynthesisUtterance(text);
        utt.rate   = chatbotConfig?.tts_speed || 1.0;
        utt.onend  = resolve;
        utt.onerror = resolve;
        window.speechSynthesis.speak(utt);
      });
    } else {
      // openai or kokoro — both served from /api/voice/:id/speak
      try {
        const res = await fetch(`/api/voice/${chatbotId}/speak`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            voice: chatbotConfig?.tts_voice || 'nova',
            speed: chatbotConfig?.tts_speed || 1.0,
          }),
        });
        if (!res.ok) throw new Error();
        const blob  = await res.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        currentAudioRef.current = audio;
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play();
        });
      } catch { /* silently fall through */ }
    }

    setPhase(STATE.IDLE);
  };

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="vmo">
      {/* Header */}
      <div className="vmo-header">
        <button className="vmo-back" onClick={onClose}>
          ← Back to chat
        </button>
        <span className="vmo-title">{chatbotName}</span>
        <span className="vmo-provider-tag">
          {ttsProvider === 'openai'  && '✦ OpenAI TTS'}
          {ttsProvider === 'browser' && '✦ Browser TTS'}
          {ttsProvider === 'none'    && '✦ Voice Input'}
        </span>
      </div>

      {/* Main */}
      <div className="vmo-body">
        {/* Orb */}
        <div className={`vmo-orb-wrap vmo-orb-${phase}`} onClick={handleOrbTap}>
          {/* Expanding rings while listening */}
          {phase === STATE.LISTENING && (
            <>
              <span className="vmo-ring vmo-ring-1" />
              <span className="vmo-ring vmo-ring-2" />
              <span className="vmo-ring vmo-ring-3" />
            </>
          )}
          <div className="vmo-orb">
            {phase === STATE.THINKING && <span className="vmo-spinner" />}
            {phase === STATE.SPEAKING && (
              <div className="vmo-bars">
                <span /><span /><span /><span /><span />
              </div>
            )}
            {(phase === STATE.IDLE || phase === STATE.LISTENING) && (
              <svg className="vmo-mic-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            )}
          </div>
        </div>

        {/* State label */}
        <p className="vmo-label">{LABELS[phase]}</p>
        {phase === STATE.IDLE && ttsProvider !== 'none' && (
          <p className="vmo-hint">Tap the orb to speak · tap again to stop early</p>
        )}
        {phase === STATE.SPEAKING && (
          <p className="vmo-hint">Tap to interrupt</p>
        )}

        {/* Subtitles */}
        {(userText || botText) && (
          <div className="vmo-subtitles">
            {userText && (
              <div className="vmo-sub vmo-sub-user">
                <span className="vmo-sub-who">You</span>
                <span className="vmo-sub-text">{userText}</span>
              </div>
            )}
            {botText && (
              <div className="vmo-sub vmo-sub-bot">
                <span className="vmo-sub-who">{chatbotName}</span>
                <span className="vmo-sub-text">{botText}</span>
              </div>
            )}
          </div>
        )}

        {phase === STATE.IDLE && (userText || botText) && (
          <button className="vmo-clear-btn" onClick={() => { setUserText(''); setBotText(''); }}>
            Clear conversation
          </button>
        )}
      </div>
    </div>
  );
}
